import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { Alert } from '@mui/material';

import { GET_PROPERTY_APPRAISABLE } from '@modules/_shared/components/appraisal/appraisalQueries';
import {
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import {
  getDefaultPropertyDetailsFormValues,
  preparePropertyDetailsFormData,
} from '@modules/_shared/components/property-form/forms-definitions/propertyDetailsFormDefinition';
import { getValidatePropertyDetailsFormData } from '@modules/_shared/components/property-form/forms-definitions/propertyDetailsFormValidation';
import {
  getDefaultPropertyFeaturesEquipmentsFormValues,
  preparePropertyFeaturesFormData,
} from '@modules/_shared/components/property-form/forms-definitions/propertyFeaturesDefinition';
import { getDefaultPropertyPoiFormValues } from '@modules/_shared/components/property-form/forms-definitions/propertyPoiDefinition';
import {
  getDefaultPropertyPricingFormValues,
  getValidatePricingFormData,
  preparePricingFormData,
} from '@modules/_shared/components/property-form/forms-definitions/propertyPricingDefinition';
import {
  type PropertyFormData,
  getPropertyFormDefinition,
} from '@modules/_shared/components/property-form/propertyFormDefinition';
import {
  GET_PROPERTY,
  UPDATE_PROPERTY,
} from '@modules/_shared/components/property-form/propertyFormQueries';
import { PropertyTypeProvider } from '@modules/_shared/components/property-form/propertyTypeProvider';
import { QueryStateView } from '@modules/_shared/components/QueryStateView';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useDebouncedHandler } from '@shared/hooks/debounce';
import { useLocale } from '@shared/hooks/locale';

export const PropertyForm = ({
  propertyId,
  readOnly = false,
  onCancel,
  onSave,
  excludeCategories,
  actionButtonsComponent,
  compactMode,
  collapsibleCategories,
  autoSave,
  onLoadingChange,
  isCMA,
}: {
  propertyId?: string;
  readOnly?: boolean;
  onSave?: () => void;
  onCancel?: (isDirty: boolean) => void;
  excludeCategories?: string[];
  actionButtonsComponent?: React.ReactNode;
  compactMode?: boolean;
  collapsibleCategories?: Array<{ name: string; count: number }>;
  autoSave?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  isCMA?: boolean;
}) => {
  const { t, locale, countryCode } = useLocale();
  const { data, loading, error } = useQuery(GET_PROPERTY, {
    variables: { id: propertyId ?? '' },
    skip: propertyId == null,
  });
  const { me } = useAppData();
  const isCHTenant = me?.tenant.country_code === 'CH';
  const isEsTenant = me?.tenant.country_code === 'ES';
  const isAddressExcluded =
    excludeCategories?.includes('address-title') === true;

  // AbortController to cancel previous mutations
  const abortControllerRef = useRef<AbortController | null>(null);

  const property = data?.properties_by_pk;
  const property_types = data?.property_types ?? [];
  const heating_types = data?.heating_enum ?? [];
  const heating_distribution = data?.heating_distribution_enum ?? [];
  const cooling_types = data?.cooling_enum ?? [];

  const [updateProperty, { loading: isAutoSaving }] = useMutation(
    UPDATE_PROPERTY,
    {
      refetchQueries: [
        {
          query: GET_PROPERTY_APPRAISABLE,
          variables: { propertyId },
        },
      ],
      awaitRefetchQueries: true,
    },
  );

  useEffect(() => {
    onLoadingChange?.(isAutoSaving);
  }, [isAutoSaving, onLoadingChange]);

  const performUpdate = useCallback(
    (formData: PropertyFormData, unstrippedFormData: PropertyFormData) => {
      if (propertyId == null) {
        return Promise.resolve();
      }

      // Abort previous mutation if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this mutation
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const {
        distance_to_airport,
        distance_to_hospital,
        distance_to_motorway,
        distance_to_city_center,
        distance_to_high_school,
        distance_to_grocery_shop,
        distance_to_kindergarten,
        distance_to_sports_center,
        distance_to_train_station,
        distance_to_primary_school,
        distance_to_public_transport,
        street_number,
        route,
        locality,
        state,
        country,
        country_code,
        postcode,
        lat,
        lng,
        ...restData
      } = formData;

      const preparedDetailsData = preparePropertyDetailsFormData(
        unstrippedFormData,
        isCHTenant,
      );

      const preparedPricingData = preparePricingFormData(
        restData,
        unstrippedFormData.__property_type,
      );

      const preparedFeaturesData = preparePropertyFeaturesFormData(
        restData,
        unstrippedFormData.__property_type,
      );

      const addressData = isAddressExcluded
        ? {}
        : {
            street_number,
            route,
            locality,
            state,
            country,
            country_code,
            postcode,
            lat,
            lng,
          };

      return updateProperty({
        variables: {
          id: propertyId,
          property: {
            distance_to_airport,
            distance_to_hospital,
            distance_to_motorway,
            distance_to_city_center,
            distance_to_high_school,
            distance_to_grocery_shop,
            distance_to_kindergarten,
            distance_to_sports_center,
            distance_to_train_station,
            distance_to_primary_school,
            distance_to_public_transport,
            ...addressData,
            ...preparedFeaturesData,
            ...preparedPricingData,
            ...preparedDetailsData,
          },
        },
        context: {
          fetchOptions: {
            signal: controller.signal,
          },
        },
        onCompleted: () => {
          onSave?.();
        },
      });
    },
    [propertyId, updateProperty, isCHTenant, isAddressExcluded, onSave],
  );

  const debouncedUpdate = useDebouncedHandler(300, performUpdate);

  const onChangeHandler: RaFormOnChange<PropertyFormData> = useCallback(
    (formData, name, type, helpers) => {
      if (name === '__main_type') {
        helpers.setValue('property_type_id', null, {
          shouldValidate: true,
          shouldDirty: true,
        });
        return;
      }

      if (type === 'change' && autoSave) {
        const typedFormData = formData as PropertyFormData;
        const isValid = helpers.runValidation();

        if (isValid) {
          debouncedUpdate(typedFormData, typedFormData);
        }
      }
    },
    [autoSave, debouncedUpdate],
  );

  const defaultValues = useMemo<PropertyFormData | {}>(() => {
    if (property != null) {
      const {
        street_number,
        route,
        locality,
        state,
        country,
        country_code,
        postcode,
        lat,
        lng,
        created_at,
        created_by,
        ...propertyData
      } = property;

      return {
        ...(!isAddressExcluded && {
          street_number,
          route,
          locality,
          state,
          country,
          country_code,
          postcode,
          lat,
          lng,
        }),
        created_at,
        created_by,
        ...getDefaultPropertyPricingFormValues(propertyData),
        ...getDefaultPropertyPoiFormValues(propertyData),
        ...getDefaultPropertyFeaturesEquipmentsFormValues(propertyData),
        ...getDefaultPropertyDetailsFormValues(propertyData),
      };
    }

    return {};
  }, [property, isAddressExcluded]);

  const propertyFormDefinition = useMemo(
    () =>
      getPropertyFormDefinition(
        isCHTenant,
        null,
        property?.country_code ?? countryCode,
        isCHTenant,
        locale,
        excludeCategories,
        compactMode,
        isCMA,
        isEsTenant,
      ),
    [
      isCHTenant,
      property,
      locale,
      countryCode,
      excludeCategories,
      compactMode,
      isCMA,
      isEsTenant,
    ],
  );

  if (loading || error) {
    return <QueryStateView loading={loading} error={error} />;
  }

  if (!property) {
    return <Alert severity="error">{t('Property not found')}</Alert>;
  }

  return (
    data && (
      <RaForm
        formDefinition={propertyFormDefinition}
        readOnly={readOnly}
        context={{
          propertyTypes: property_types,
          heatingTypes: heating_types,
          heatingDistribution: heating_distribution,
          coolingTypes: cooling_types,
          propertyId: property?.id,
          readOnly,
        }}
        onCancel={onCancel}
        actionButtonsComponent={actionButtonsComponent}
        collapsibleCategories={collapsibleCategories}
        defaultValues={defaultValues}
        onChange={onChangeHandler}
        onSubmit={performUpdate}
        validate={data => {
          const pricingErrors = getValidatePricingFormData(
            t,
            data.__property_type,
          )(data);
          const detailsErrors = getValidatePropertyDetailsFormData(t)(data);

          return [...pricingErrors, ...detailsErrors];
        }}
      >
        <PropertyTypeProvider propertyTypes={property_types} />
      </RaForm>
    )
  );
};
